import { CheckCircle2, Server } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCreateServer } from '@/hooks/use-create-server';
import { useDispatchPreflight, usePreflightResult } from '@/hooks/use-preflight';
import { useProvisionServer } from '@/hooks/use-provision-server';
import { ApiError } from '@/lib/api-client';

import { EMPTY_WIZARD_FORM, StepConnection, type WizardForm } from './step-connection';
import { StepLocation } from './step-location';
import { StepPreflight } from './step-preflight';
import { StepReview } from './step-review';

interface StepMeta {
  title: string;
  subtitle: string;
}

const STEPS: StepMeta[] = [
  { title: 'Connection', subtitle: 'Identity, network details and SSH credentials' },
  { title: 'Location', subtitle: 'Confirm the geographic region for this server' },
  { title: 'Preflight', subtitle: 'Verify SSH access and check system requirements' },
  { title: 'Review & launch', subtitle: 'Confirm configuration and start provisioning' },
];

interface StepperProps {
  current: number;
  total: number;
}

function Stepper({ current, total }: StepperProps): JSX.Element {
  return (
    <div className="flex items-center gap-0">
      {Array.from({ length: total }, (_, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div
              className={[
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
                done
                  ? 'border-primary bg-primary text-primary-foreground'
                  : active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-transparent text-muted-foreground/50',
              ].join(' ')}
            >
              {done ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : i + 1}
            </div>
            {i < total - 1 && (
              <div
                className={[
                  'h-0.5 flex-1 mx-1 transition-colors',
                  i < current ? 'bg-primary' : 'bg-border',
                ].join(' ')}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ProvisionVpnWizard(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardForm>({ ...EMPTY_WIZARD_FORM });
  const [createdName, setCreatedName] = useState<string | null>(null);
  const [preflightDispatched, setPreflightDispatched] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const createServer = useCreateServer();
  const dispatchPreflight = useDispatchPreflight();
  const provision = useProvisionServer();
  const preflight = usePreflightResult(createdName ?? undefined, preflightDispatched);

  const patch = useCallback((p: Partial<WizardForm>) => {
    setForm((current) => {
      if ('name' in p && p.name !== current.name) {
        setCreatedName(null);
        setPreflightDispatched(false);
      }
      return { ...current, ...p };
    });
  }, []);

  function reset(): void {
    setStep(0);
    setForm({ ...EMPTY_WIZARD_FORM });
    setCreatedName(null);
    setPreflightDispatched(false);
    setAcknowledged(false);
    setError(null);
  }

  function close(): void {
    setOpen(false);
    reset();
  }

  const step1Valid =
    form.name.trim() !== '' &&
    form.provider.trim() !== '' &&
    form.public_ip.trim() !== '' &&
    form.ssh_username.trim() !== '' &&
    form.ssh_password !== '' &&
    // proxy_ingress serves HTTPS-CONNECT on a TLS hostname — required.
    (form.role !== 'proxy_ingress' || form.proxy_hostname.trim() !== '');

  function runPreflight(): void {
    setError(null);
    const name = form.name.trim();

    const proceed = (): void => {
      dispatchPreflight.mutate(name, {
        onSuccess: () => {
          setCreatedName(name);
          setPreflightDispatched(true);
        },
        onError: (err) => setError(err.message),
      });
    };

    if (createdName === name) {
      proceed();
      return;
    }

    createServer.mutate(
      {
        name,
        role: form.role,
        public_ip: form.public_ip.trim(),
        provider: form.provider.trim(),
        vm_type: form.vm_type.trim() || undefined,
        private_ip: form.private_ip.trim() || undefined,
        ssh_username: form.ssh_username.trim(),
        ssh_password: form.ssh_password,
        region_continent: form.region_continent.trim() || undefined,
        region_country: form.region_country.trim() || undefined,
        region_city: form.region_city.trim() || undefined,
        auto_provision: false,
        // VPN-only: a Reality edge runs Xray/VLESS-Reality, whose transport is
        // generated on the host during deploy — no protocol/mode at create time.
        ...(form.role === 'vpn_server'
          ? { connection_mode: form.connection_mode, protocols: [{ protocol: form.protocol }] }
          : {}),
        // proxy_ingress: the HTTPS-CONNECT hostname is stored on the row as
        // dns_hostname; the provision survey maps it to proxy_hostname.
        ...(form.role === 'proxy_ingress'
          ? { dns_hostname: form.proxy_hostname.trim() }
          : {}),
      },
      {
        onSuccess: proceed,
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : 'Could not create the server row.'),
      },
    );
  }

  function launch(): void {
    if (!createdName) return;
    provision.mutate(createdName, {
      onSuccess: ({ task_id }) => {
        toast.success(`Provisioning ${createdName} dispatched (task #${task_id}).`);
        const name = createdName;
        close();
        navigate(`/fleet/${encodeURIComponent(name)}`);
      },
      onError: (err) => setError(err.message),
    });
  }

  const requirementsUnmet =
    preflight.data === null ||
    preflight.data === undefined ||
    preflight.data.requirements === null ||
    !preflight.data.requirements.meets;
  const launchBlocked = requirementsUnmet && !acknowledged;

  const currentStep = STEPS[step];

  return (
    <>
      <Button size="sm" variant="default" onClick={() => setOpen(true)}>
        <Server className="h-4 w-4 mr-2" aria-hidden />
        Provision server
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="sr-only">Provision a server</DialogTitle>
          </DialogHeader>

          {/* Stepper */}
          <Stepper current={step} total={STEPS.length} />

          {/* Step header */}
          <div>
            <p className="text-xs text-muted-foreground font-medium">Step {step + 1}</p>
            <h2 className="text-base font-semibold">{currentStep.title}</h2>
            <p className="text-sm text-muted-foreground">{currentStep.subtitle}</p>
          </div>

          {/* Step body */}
          {step === 0 && <StepConnection form={form} onChange={patch} />}
          {step === 1 && <StepLocation form={form} onChange={patch} />}
          {step === 2 && (
            <StepPreflight
              result={preflight.data ?? null}
              isDispatching={createServer.isPending || dispatchPreflight.isPending}
              error={error}
              onRun={runPreflight}
            />
          )}
          {step === 3 && (
            <StepReview
              form={form}
              preflight={preflight.data ?? null}
              acknowledged={acknowledged}
              onAcknowledge={setAcknowledged}
            />
          )}

          {error && step !== 2 && (
            <p className="text-sm text-destructive" role="alert">{error}</p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => (step === 0 ? close() : setStep(step - 1))}
              >
                {step === 0 ? 'Cancel' : 'Back'}
              </Button>

              {step < 3 && (
                <Button
                  type="button"
                  disabled={
                    (step === 0 && !step1Valid) ||
                    (step === 2 &&
                      preflight.data?.status !== 'success' &&
                      preflight.data?.status !== 'failed')
                  }
                  onClick={() => setStep(step + 1)}
                >
                  Next
                </Button>
              )}
              {step === 3 && (
                <Button
                  type="button"
                  disabled={launchBlocked || provision.isPending}
                  onClick={launch}
                >
                  {provision.isPending
                    ? 'Launching…'
                    : `Launch ${
                        form.role === 'v2ray_edge'
                          ? 'Reality edge'
                          : form.role === 'proxy_ingress'
                            ? 'Proxy ingress'
                            : form.role === 'bootstrap_edge'
                              ? 'Bootstrap edge'
                              : 'VPN server'
                      }`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
